import React from 'react';
import PropTypes from 'prop-types';
import { Oval } from 'react-loader-spinner';
import { ArrowRight, Edit } from 'lucide-react';
import SubscriptionToggle from '../cart/SubscriptionToggle';
import Button from '../ui/Button';
import ProductPrice from './ProductPrice';
import './ProductSlider.scss';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { setCustomizedProduct, setPetType } from '../../store/customizerSlice';
import { formatLabel } from '../../utils/formatLabel';
import "./OnProductImageText.scss";
import { useDispatch } from 'react-redux';


const ProductBox = ({
  product,
  finalImages,
  subscriptions,
  openModal,
  toggleSubscription,
  handleAddToCart,
  editButton = false,
  fastCheckoutButton = false,
  designerMode = false,
  basicProductImage = false,
  resultAffiliateImageId = null,
  customProductTitle = null,
  className
}) => {
  const dispatch = useDispatch();
  const navigate = useAppNavigate();

  return (
    <div key={product.id} className={`product-box flex flex-col rounded-20 p-4 box-shadow ${className}`}>
      <div className="product-image-cont mb-3">
        {product.flavor && (
          <div className={`product-tag capitalize ${product.category.toLowerCase()}`}>
            {formatLabel(product.flavor)}
          </div>
        )}

        {!basicProductImage && !finalImages[product.id] && !designerMode ? (
          <div className="flex items-center justify-center h-full w-full">
            <Oval
              visible={true}
              height={50}
              width={50}
              color="white"
              strokeWidth={3}
              strokeWidthSecondary={3}
              secondaryColor="#F6F6FC"
              ariaLabel="oval-loading"
            />
          </div>
        ) : (!basicProductImage && (finalImages[product.id] || designerMode)) ? (
          <div onClick={() => { openModal(product) }} className="product-details-img cursor-pointer">
            <img
              key={finalImages[product.id] || `mockup-${product.id}`}
              src={finalImages[product.id] || product.image}
              alt={product.title}
              className="w-full object-cover rounded"
            />
          </div>
        )
          :
          <div onClick={() => navigate(`/product/${product.sku}`)} className="product-details-img cursor-pointer">
            <div className="product-image-overlay">
              <img
                key={`mockup-${product.id}`}
                src={product.image}
                alt={customProductTitle || product.title}
                className="w-full object-cover rounded"
              />
              <div className="overlay-text">Personalize With Your Pet's Photo!</div>
            </div>
          </div>
        }
      </div>

      <div
        className="flex justify-between cursor-pointer gap-"
        onClick={() => {
          if (basicProductImage) {
            navigate(`/product/${product.sku}`);
          } else if (finalImages[product.id]) {
            openModal(product);
          }
        }}
      >
        <p className="text-md product-title min-h-[45px] mb-2 font-bold">{customProductTitle || product.title}</p>
        <ProductPrice price={product.price} isSubscribed={toggleSubscription ? subscriptions[product.id] : true} />
      </div>

      {product.shortDescription ? (
        <p className="text-sm mb-2 capitalize flex-1">{product.shortDescription}</p>
      ) : (
        <br />
      )}

      <div>
        {toggleSubscription && !basicProductImage &&
          <SubscriptionToggle
            isSubscribed={subscriptions[product.id] || false}
            onToggle={() => toggleSubscription(product.id)}
            className="mb-4"
          />
        }

        {basicProductImage ?
          <Button
            variant="filled"
            className="justify-between"
            type="button"
            onClick={() => navigate(`/product/${product.sku}`)}
          >
            View Product
            <ArrowRight size={27} />
          </Button>
          :
          <Button
            variant="filled"
            className="justify-between"
            type="button"
            disabled={!designerMode && !finalImages[product.id]}
            onClick={() => handleAddToCart(product, subscriptions[product.id], 1, resultAffiliateImageId, customProductTitle)}
          >
            {designerMode ? "Customize" : "Add to Cart"}
            <ArrowRight size={27} />
          </Button>
        }

        {editButton &&
          <Button
            type="button"
            onClick={() => navigate('/customizer')}
            variant="outlined"
            className="mt-5 justify-between"
          >
            <span>Personalize</span>
            <Edit size={20} />
          </Button>
        }

        {fastCheckoutButton &&
          <Button
            type="button"
            onClick={() => {
              dispatch(setCustomizedProduct({ product, isSubscribed: subscriptions[product.id], quantity: 1 }));
              dispatch(setPetType(product.petType));
              navigate('/customizer');
            }}
            variant="outlined"
            className="mt-5 justify-between"
          >
            <span>Personalize</span>
            <Edit size={20} />
          </Button>
        }
      </div>
    </div>
  );
};

ProductBox.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    flavor: PropTypes.string,
    price: PropTypes.number.isRequired,
    sku: PropTypes.string,
    petType: PropTypes.string,
    image: PropTypes.string
  }).isRequired,
  finalImages: PropTypes.object,
  subscriptions: PropTypes.object,
  openModal: PropTypes.func,
  toggleSubscription: PropTypes.func,
  handleAddToCart: PropTypes.func,
  editButton: PropTypes.bool,
  fastCheckoutButton: PropTypes.bool,
  basicProductImage: PropTypes.bool,
  resultAffiliateImageId: PropTypes.string,
  customProductTitle: PropTypes.string,
  className: PropTypes.string
};

export default ProductBox;
